import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { CtaBand } from "../../../components/site/CtaBand";
import { Lightbox, type LightboxItem } from "../../../components/site/Lightbox";
import { ProductCard, SiteImage } from "../../../components/site/ProductCard";
import { SiteIcon } from "../../../components/site/SiteIcon";
import { PRODUCTS, productBySlug, type Product } from "../../../content/products";
import { BRANDS, BUSINESS, IMAGES, quotePath } from "../../../content/site";

/** BR-WEB-001 — exactly these four, in this order; the first is the large card. */
export const FEATURED_SLUGS = ["opening-roof-vergolas", "patios", "decks", "ziptrak-blinds"] as const;

const IMG = "/images/website/";
const WORK: readonly LightboxItem[] = [
  "openingroof/vergolas3001.jpg",
  "insulatedroof/gable-patiol-5lq.jpg",
  "decks/img-0022.jpg",
  "ziptrackblinds/gwp-zip-mesh-29.jpg",
  "outdoorrooms/img-0276.jpg",
].map((src) => ({ src: IMG + src, cap: "Greatway Patios project" }));

/**
 * Home — replaces legacy "/" (Story #25439). Copy is the legacy site's, tightened without adding
 * claims (baseline § 5); credentials are exactly the legacy ones and no year of establishment is
 * shown (BR-WEB-006). Every quote CTA routes to /enquire (BR-WEB-002).
 */
export default function HomePage() {
  // A withdrawn product is simply omitted and the grid reflows (Empty state).
  const featured = FEATURED_SLUGS.map((s) => productBySlug(s)).filter((p): p is Product => Boolean(p));
  const [photo, setPhoto] = useState<number | null>(null);
  const closePhoto = useCallback(() => setPhoto(null), []);

  return (
    <>
      <section className="gw-hero" aria-labelledby="home-title">
        <SiteImage
          src={IMAGES.bannerAbout}
          alt="Timber deck dining area under a Greatway patio, enclosed with mesh blinds"
          className="gw-hero-img"
          eager
        />
        <div className="gw-wrap gw-hero-in">
          <p className="gw-eyebrow light">Toowoomba · Darling Downs · Lockyer Valley</p>
          <h1 id="home-title">
            Outdoor living, <em>built to last.</em>
          </h1>
          <p className="gw-lead">
            Patios, opening roofs, decks, carports and screens — designed, council-approved and
            installed by a locally owned family business with over 25 years’ experience.
          </p>
          <div className="gw-actions">
            <Link className="gw-btn gw-btn-red gw-btn-lg" to={quotePath()}>
              Get a free measure &amp; quote <SiteIcon name="arrow" size={18} />
            </Link>
            <Link className="gw-btn gw-btn-ghost gw-btn-lg" to="/products">
              Explore products
            </Link>
          </div>
        </div>
        <div className="gw-hero-card">
          <b>Visit our display centre</b>
          <div>
            <SiteIcon name="pin" size={16} /> {BUSINESS.street}, {BUSINESS.town}
          </div>
          <div>
            <SiteIcon name="clock" size={16} /> {BUSINESS.hours}
          </div>
          <Link className="gw-link" style={{ marginTop: 10 }} to="/contact">
            Plan your visit <SiteIcon name="arrow" size={16} />
          </Link>
        </div>
      </section>

      <section className="gw-creds" aria-label="Why Greatway Patios">
        <ul className="gw-wrap" style={{ listStyle: "none", margin: "0 auto" }}>
          <li className="gw-cred">
            <SiteIcon name="house" size={26} />
            <span>
              <b>Family owned</b>Locally owned &amp; managed
            </span>
          </li>
          <li className="gw-cred">
            <SiteIcon name="shield" size={26} />
            <span>
              <b>QBCC licensed</b>Builder licence {BUSINESS.qbcc}
            </span>
          </li>
          <li className="gw-cred">
            <SiteIcon name="award" size={26} />
            <span>
              <b>Master Builders</b>Queensland member
            </span>
          </li>
          <li className="gw-cred">
            <SiteIcon name="doc" size={26} />
            <span>
              <b>Permits handled</b>We take care of council
            </span>
          </li>
          <li className="gw-cred">
            <SiteIcon name="ruler" size={26} />
            <span>
              <b>Free measure</b>&amp; obligation-free quote
            </span>
          </li>
        </ul>
      </section>

      <section className="gw-sec" aria-labelledby="home-who">
        <div className="gw-wrap">
          <div className="gw-intro">
            <div>
              <p className="gw-eyebrow">Who we are</p>
              <h2 id="home-who" className="big" style={{ fontWeight: 400 }}>
                The largest outdoor patio building specialists on the <em>Darling Downs.</em>
              </h2>
            </div>
            <div>
              <p>
                We specialise in creating outdoor areas that are purpose-built to your requirements —
                from small budget patios to large-scale domestic and commercial projects — backed by
                the most comprehensive range of quality patio roofing systems and outdoor products in
                the region.
              </p>
              <p>
                As winners of National, State and Local awards, we’re confident that together we can
                design an addition to your home that suits your architecture, lifestyle and budget.
              </p>
              <Link className="gw-link" to="/about">
                Our story <SiteIcon name="arrow" size={16} />
              </Link>
            </div>
          </div>
          <dl className="gw-stats">
            <div className="gw-stat">
              <dt className="sr-only">Experience</dt>
              <dd style={{ margin: 0 }}>
                <b>25+</b>
                <span>Years’ experience, trading from the same South Street site</span>
              </dd>
            </div>
            <div className="gw-stat">
              <dt className="sr-only">Product ranges</dt>
              <dd style={{ margin: 0 }}>
                <b>{PRODUCTS.length}</b>
                <span>Outdoor living product ranges, supplied and installed</span>
              </dd>
            </div>
            <div className="gw-stat">
              <dt className="sr-only">Display centre</dt>
              <dd style={{ margin: 0 }}>
                <b>1</b>
                <span>Toowoomba’s only outdoor display centre</span>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="gw-sec sand" aria-labelledby="home-products">
        <div className="gw-wrap">
          <div className="gw-sechead">
            <div>
              <p className="gw-eyebrow">Our products</p>
              <h2 id="home-products" className="gw-h2">
                Everything for outdoor living — under one roof.
              </h2>
            </div>
            <Link className="gw-link" to="/products">
              All {PRODUCTS.length} products <SiteIcon name="arrow" size={16} />
            </Link>
          </div>
          <div className="gw-bento" data-testid="featured-products">
            {featured.map((p, i) => (
              <ProductCard key={p.slug} product={p} large={i === 0} />
            ))}
            <Link className="gw-alltile" to="/products">
              <span className="gw-cat">{PRODUCTS.length} product ranges</span>
              <h3>See everything we build</h3>
              <p>Insulated roofs, carports, outdoor rooms, louvres, lattice, awnings, glass balustrade and more.</p>
              <span className="gw-more">
                All products <SiteIcon name="arrow" size={16} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="gw-sec dark" aria-labelledby="home-dc">
        <div className="gw-wrap gw-dc">
          <div className="gw-dc-media">
            <SiteImage src={IMAGES.homeVergola} alt="Vergola opening roof over an outdoor living area" />
            <div className="badge">
              <b>See it first</b>Every product on display, in one place
            </div>
          </div>
          <div>
            <p className="gw-eyebrow">The display centre</p>
            <h2 id="home-dc">See it and touch it before you build it.</h2>
            <p>
              Customers like to view and touch the products they’re interested in — so we built a
              display centre showcasing our whole range in one place, with product samples and
              photographs of work across the Darling Downs. Walk around at your leisure, or let us
              come to you.
            </p>
            <blockquote className="gw-quote">
              “We don’t work out of the back of a ute. Our whole focus is on providing our customers
              with quality products and exceptional service.”
              <cite>Paul Cooper, Director</cite>
            </blockquote>
            <div className="gw-meta">
              <span>
                <SiteIcon name="pin" size={18} /> {BUSINESS.street}, {BUSINESS.town} {BUSINESS.state}{" "}
                {BUSINESS.postcode}
              </span>
              <span>
                <SiteIcon name="clock" size={18} /> {BUSINESS.hours}
              </span>
            </div>
            <div className="gw-actions">
              <Link className="gw-btn gw-btn-red" to={quotePath()}>
                Book free design advice
              </Link>
              <Link className="gw-btn gw-btn-ghost" to="/contact">
                Get directions
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="gw-sec" aria-labelledby="home-how">
        <div className="gw-wrap">
          <div className="gw-sechead">
            <div>
              <p className="gw-eyebrow">How it works</p>
              <h2 id="home-how" className="gw-h2">
                From first idea to finished build.
              </h2>
            </div>
          </div>
          <ol className="gw-steps" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            <li className="gw-step">
              <h3>Free measure &amp; quote</h3>
              <p>Call or send an enquiry and we’ll measure up on site — no cost, no obligation.</p>
            </li>
            <li className="gw-step">
              <h3>Friendly design advice</h3>
              <p>
                Visit the display centre for inspiration, or our consultants come to you. They’re
                trained in every product and the building process.
              </p>
            </li>
            <li className="gw-step">
              <h3>Council permits handled</h3>
              <p>We take care of all the necessary council requirements, so the paperwork isn’t yours to chase.</p>
            </li>
            <li className="gw-step">
              <h3>Built by qualified trades</h3>
              <p>
                Installed by fully trained, qualified tradesmen, supervised on site by our licensed
                builder — then backed by our after-sales care.
              </p>
            </li>
          </ol>
          <div className="gw-kit">
            <SiteIcon name="tag" size={22} />
            <span>
              <b>Prefer to build it yourself?</b> Ask about kit-only supply pricing.
            </span>
          </div>
        </div>
      </section>

      <section className="gw-sec tight sand" aria-labelledby="home-work">
        <div className="gw-wrap">
          <div className="gw-sechead">
            <div>
              <p className="gw-eyebrow">Our work</p>
              <h2 id="home-work" className="gw-h2">
                Built across the Darling Downs.
              </h2>
            </div>
            <Link className="gw-link" to="/our-work">
              See all our work <SiteIcon name="arrow" size={16} />
            </Link>
          </div>
          <div className="gw-mosaic">
            {WORK.map((w, i) => (
              <button
                key={w.src}
                type="button"
                onClick={() => setPhoto(i)}
                aria-label={`Open photo ${i + 1} of ${WORK.length}`}
              >
                <SiteImage src={w.src} alt={w.cap} />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="gw-sec tight" aria-labelledby="home-brands">
        <div className="gw-wrap">
          <p id="home-brands" className="gw-eyebrow" style={{ justifyContent: "center" }}>
            Quality brands we install
          </p>
          <div className="gw-brands">
            {BRANDS.map((b) => (
              <SiteImage key={b.name} src={b.logo} alt={`${b.name} logo`} />
            ))}
          </div>
        </div>
      </section>

      <CtaBand />

      <Lightbox items={WORK} index={photo} onClose={closePhoto} onIndexChange={setPhoto} />
    </>
  );
}
